import React, { useState } from 'react';
import { INDUSTRY_DOMAINS, DOMAIN_METADATA } from '../constants/domains';
import { X } from 'lucide-react';
import './DomainSelector.css';


function DomainSelector({ selectedDomains, onDomainsChange, maxSelection = 3, required = false }) {
    const [currentSelection, setCurrentSelection] = useState('');
    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 4000);
    };

    const handleAddDomain = (e) => {
        e.preventDefault();
        const domain = currentSelection;

        if (!domain || domain === '') return;

        if (selectedDomains.includes(domain)) {
            showToast('This domain is already selected');
            return;
        }

        if (selectedDomains.length >= maxSelection) {
            showToast(`You can only select up to ${maxSelection} domains`);
            return;
        }

        onDomainsChange([...selectedDomains, domain]);
        setCurrentSelection('');
    };

    const handleRemoveDomain = (domainToRemove) => {
        onDomainsChange(selectedDomains.filter(d => d !== domainToRemove));
    };

    const clearAll = () => {
        onDomainsChange([]);
    };

    // Get available domains (not yet selected)
    const availableDomains = INDUSTRY_DOMAINS.filter(d => !selectedDomains.includes(d));

    return (
        <div className="domain-selector-container">
            <div className="domain-selector-header">
                <div className="header-content">
                    <h3>Choose Your Interests</h3>
                    <p>Select up to {maxSelection} domains that match your career interests</p>
                </div>
                {selectedDomains.length > 0 && (
                    <button type="button" className="clear-all-btn" onClick={clearAll}>
                        <X size={16} /> Clear All
                    </button>
                )}
            </div>

            <div className="domain-dropdown-section">
                <label className="dropdown-label">Add Domain</label>
                <div className="dropdown-row">
                    <select
                        className="domain-dropdown"
                        value={currentSelection}
                        onChange={(e) => setCurrentSelection(e.target.value)}
                        disabled={selectedDomains.length >= maxSelection}
                    >
                        <option value="">-- Select a domain --</option>
                        {availableDomains.map(domain => {
                            const metadata = DOMAIN_METADATA[domain] || { icon: '📚', description: '' };
                            return (
                                <option key={domain} value={domain}>
                                    {metadata.icon} {domain}
                                </option>
                            );
                        })}
                    </select>
                    <button
                        type="button"
                        className="add-domain-btn"
                        onClick={handleAddDomain}
                        disabled={!currentSelection || selectedDomains.length >= maxSelection}
                    >
                        Add
                    </button>
                </div>
            </div>

            <div className="selected-count">
                {selectedDomains.length} / {maxSelection} selected
                {selectedDomains.length >= maxSelection && (
                    <span className="limit-reached"> (Maximum reached)</span>
                )}
            </div>

            {selectedDomains.length > 0 && (
                <div className="selected-domains-list">
                    <label className="list-label">Your Selected Domains:</label>
                    {selectedDomains.map((domain, index) => {
                        const metadata = DOMAIN_METADATA[domain] || { icon: '📚', description: '', color: '#6366F1' };
                        const isPrimary = index === 0;

                        return (
                            <div
                                key={domain}
                                className="selected-domain-item"
                                style={{ borderLeftColor: metadata.color }}
                            >
                                <div className="domain-item-content">
                                    <span className="domain-icon">{metadata.icon}</span>
                                    <div className="domain-item-info">
                                        <span className="domain-name">{domain}</span>
                                        {isPrimary && <span className="primary-badge-inline">Primary</span>}
                                        <span className="domain-description">{metadata.description}</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="remove-domain-btn"
                                    onClick={() => handleRemoveDomain(domain)}
                                    title="Remove domain"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="domain-selector-footer">
                <p className="footer-note">
                    💡 <strong>Tip:</strong> The first domain you select will be your primary interest. You can change these preferences anytime from your profile settings.
                </p>
            </div>
        </div>
    );
}

export default DomainSelector;
